import { Show, splitProps, type Component, type JSX } from 'solid-js';
import { cn } from '~/shared/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leading?: JSX.Element;
  trailing?: JSX.Element;
  children?: JSX.Element;
}

const variantClass: Record<Variant, string> = {
  primary:
    'bg-accent text-white hover:brightness-110 active:brightness-95 shadow-sm',
  secondary:
    'bg-white/8 text-primary hover:bg-white/12 border border-hairline',
  ghost:
    'bg-transparent text-secondary hover:bg-white/6 hover:text-primary',
  danger:
    'bg-danger text-white hover:brightness-110 active:brightness-95',
};

const sizeClass: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px] rounded-sm',
  md: 'h-10 px-4 text-[14px] rounded-md',
  lg: 'h-12 px-5 text-[15px] rounded-md font-medium',
};

export const Button: Component<ButtonProps> = (props) => {
  const [local, rest] = splitProps(props, [
    'variant',
    'size',
    'loading',
    'leading',
    'trailing',
    'children',
    'class',
    'disabled',
  ]);

  const variant = () => local.variant ?? 'primary';
  const size = () => local.size ?? 'md';
  const disabled = () => local.disabled || local.loading;

  return (
    <button
      {...rest}
      disabled={disabled()}
      class={cn(
        'press inline-flex items-center justify-center gap-2 font-medium',
        'transition-colors duration-base ease-ios',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        variantClass[variant()],
        sizeClass[size()],
        local.class,
      )}
    >
      <Show when={local.loading}>
        <Spinner />
      </Show>
      <Show when={!local.loading && local.leading}>{local.leading}</Show>
      {local.children}
      <Show when={!local.loading && local.trailing}>{local.trailing}</Show>
    </button>
  );
};

const Spinner: Component = () => (
  <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-opacity="0.25" stroke-width="2.5" />
    <path
      d="M21 12a9 9 0 0 0-9-9"
      stroke="currentColor"
      stroke-width="2.5"
      stroke-linecap="round"
    />
  </svg>
);
